import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, X, ChevronLeft, ChevronRight, Play, Image, Film, Building2, Sparkles, Wand2, Moon, Sun } from 'lucide-react'
import { useTheme } from '../context/ThemeContext'

const albums = [
  {
    id: 'ai-generated',
    title: 'AI-Generated Work',
    icon: Sparkles,
    description: 'Student projects created using AI tools — see the prompts and results.',
    cover: '/Gallery/ai-generated/Logo Design for Local Business.png',
    category: 'AI-Generated',
    items: [
      { 
        type: 'ai-prompt', 
        src: '/Gallery/ai-generated/Logo Design for Local Business.png', 
        prompt: 'Modern minimalist logo for Kampala coffee shop, incorporating traditional Ugandan patterns, warm earth tones, clean typography',
        caption: 'Logo Design for Local Business',
        tool: 'Midjourney + Adobe Illustrator'
      },
      { 
        type: 'ai-prompt', 
        src: '/Gallery/ai-generated/AI-Enhanced Product Shot.png', 
        prompt: 'Professional product photography, studio lighting, white background, commercial quality for e-commerce',
        caption: 'AI-Enhanced Product Shot',
        tool: 'Adobe Firefly + Photoshop'
      },
      { 
        type: 'ai-prompt', 
        src: '/Gallery/ai-generated/AI Color Grading.png', 
        prompt: 'Cinematic color grade, teal and orange color palette, film photography aesthetic, moody atmosphere',
        caption: 'AI Color Grading',
        tool: 'Adobe Sensei'
      },
    ],
  },
  {
    id: 'studio',
    title: 'Studio & Campus',
    icon: Building2,
    description: 'Our professional studio spaces, equipment, and campus facilities.',
    cover: '/Gallery/Academy/124414.jpg',
    category: 'Photography',
    items: [
      { type: 'image', src: '/Gallery/Academy/124414.jpg', caption: 'SMA Studio' },
      { type: 'image', src: '/Gallery/Academy/Learning In Action/DSC02406 copy.jpg', caption: 'Studio Setup' },
      { type: 'image', src: '/Gallery/DSC02420 copy.jpg', caption: 'Studio Space' },
      { type: 'image', src: '/Gallery/Academy/Creative Training Sessions/DSC02423.jpg', caption: 'Equipment Room' },
      { type: 'image', src: '/Gallery/Academy/Multimedia Workshops/DSC02424 copy.jpg', caption: 'Production Area' },
      { type: 'image', src: '/Gallery/Academy/Learning In Action/DSC02428 copy.jpg', caption: 'Workspace' },
      { type: 'image', src: '/Gallery/Academy/Learning In Action/DSC02432 copy.jpg', caption: 'Creative Space' },
      { type: 'image', src: '/Gallery/Academy/Multimedia Workshops/DSC02588.jpg', caption: 'Campus View' },
    ],
  },
  {
    id: 'photography',
    title: 'Photography Sessions',
    icon: Image,
    description: 'Behind the scenes from our photography classes and student shoots.',
    cover: '/Gallery/268A5753.JPG',
    category: 'Photography',
    items: [
      { type: 'image', src: '/Gallery/268A5753.JPG', caption: 'Photography Class' },
      { type: 'image', src: '/Gallery/Academy/Future Storytellers/Kelly (1).jpeg', caption: 'Lighting Techniques' },
      { type: 'image', src: '/Gallery/Academy/Creative Training Sessions/268A5756.JPG', caption: 'Portrait Session' },
      { type: 'image', src: '/Gallery/Academy/Creative Training Sessions/268A5756.JPG', caption: 'Hands-on Practice' },
      { type: 'image', src: '/Gallery/268A5758.JPG', caption: 'Student Work' },
    ],
  },
  {
    id: 'classrooms',
    title: 'Classrooms & Training',
    icon: Building2,
    description: 'Our modern classrooms and hands-on training sessions in action.',
    cover: '/Gallery/Academy/Class rooms/20250822_163612.jpg',
    category: 'Photography',
    items: [
      { type: 'image', src: '/Gallery/Academy/Class rooms/20250822_163612.jpg', caption: 'Classroom Session' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_164614.jpg', caption: 'Theory Class' },
      { type: 'image', src: '/Gallery/Academy/Class rooms/20250822_164638.jpg', caption: 'Group Work' },
      { type: 'image', src: '/Gallery/Academy/Class rooms/20250822_164713.jpg', caption: 'Practical Lesson' },
      { type: 'image', src: '/Gallery/Academy/Class rooms/20250822_165220.jpg', caption: 'Student Presentation' },
      { type: 'image', src: '/Gallery/Academy/Class rooms/20250822_165220.jpg', caption: 'Workshop' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_165442.jpg', caption: 'Lab Session' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_165604.jpg', caption: 'Training in Progress' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_165622.jpg', caption: 'Instructor Demo' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_165729.jpg', caption: 'Hands-on Training' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_171029.jpg', caption: 'Creative Session' },
      { type: 'image', src: '/Gallery/Class rooms/20250822_171103.jpg', caption: 'Class in Action' },
    ],
  },
  {
    id: 'videos',
    title: 'Video Showcase',
    icon: Film,
    description: 'Video tours, lesson previews, and student productions.',
    cover: '/Gallery/114500.jpg',
    category: 'Video',
    items: [
      { type: 'video', youtubeId: 'nIkutyG6vu4', caption: 'Synergy Multimedia Academy Tour | Reception & Professional Studio Overview' },
      { type: 'video', youtubeId: '35lUT3wQ_cE', caption: 'Camera Lessons in Uganda | Photography & Videography Training for Beginners' },
      { type: 'video', youtubeId: 'D_Td2RFYxiM', caption: 'Photoshop Classes in Uganda | Graphic Design Training at Synergy Multimedia Academy' },
    ],
  },
]

const categories = ['All', 'Photography', 'Video', 'AI-Generated']

export default function GalleryPage() {
  const { darkMode, toggleDarkMode } = useTheme()
  const [selectedAlbum, setSelectedAlbum] = useState(null)
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState('All')

  const filteredAlbums = selectedCategory === 'All' 
    ? albums 
    : albums.filter(album => album.category === selectedCategory)

  useEffect(() => {
    document.title = 'Student Work Gallery | Photography School Uganda | Multimedia Academy Kampala Projects'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) metaDesc.setAttribute('content', 'View student projects from Kampala\'s top Photography School & Multimedia Academy. See real work from Photography, Videography, AI Creative Courses & Graphic Design programs in Uganda.')
  }, [])

  // Close lightbox on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (lightboxIndex !== null) setLightboxIndex(null)
        else if (selectedAlbum) setSelectedAlbum(null)
      }
      if (lightboxIndex !== null && selectedAlbum) {
        if (e.key === 'ArrowRight') {
          setLightboxIndex((prev) => (prev + 1) % selectedAlbum.items.length)
        }
        if (e.key === 'ArrowLeft') {
          setLightboxIndex((prev) => (prev - 1 + selectedAlbum.items.length) % selectedAlbum.items.length)
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxIndex, selectedAlbum])

  // Prevent body scroll when lightbox open
  useEffect(() => {
    document.body.style.overflow = lightboxIndex !== null ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [lightboxIndex])

  const currentItem = lightboxIndex !== null && selectedAlbum ? selectedAlbum.items[lightboxIndex] : null

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <nav className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center gap-3">
              <img
                src={darkMode ? '/Logos/SMA LOGO  DARK MODE.png' : '/Logos/SMA LOGO  VERSION 2-01.png'}
                alt="Synergy Multimedia Academy logo"
                className="h-10 sm:h-14"
              />
              <div className="hidden sm:block">
                <p className="font-bold text-gray-900 dark:text-white text-sm leading-tight">Synergy Multimedia</p>
                <p className="text-xs text-primary-600 dark:text-primary-400 font-medium">Academy</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Toggle dark mode"
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 text-sm font-medium transition-colors"
              >
                <ArrowLeft size={18} />
                Back to Home
              </Link>
              <Link
                to="/register"
                className="hidden sm:inline-flex bg-primary-500 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors shadow-md shadow-primary-500/25"
              >
                Enroll Now
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 py-16 sm:py-24">
        <div className="absolute inset-0 opacity-20">
          <picture>
            <source srcSet="/Gallery/DSC02588.webp" type="image/webp" />
            <img src="/Gallery/DSC02588.jpg" alt="Synergy Multimedia Academy campus exterior - modern creative training facility in Kampala Uganda" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </picture>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-white/90 text-sm font-medium mb-6">
            Life at SMA
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4">
            Student Portfolio <span className="text-primary-400">Gallery</span> — Photography School & Multimedia Academy Kampala
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Explore our campus, classrooms, studio facilities, and see our students and instructors in action.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {!selectedAlbum ? (
          <div>
            {/* Category Filter Bar */}
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-8 sm:mb-12">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${
                    selectedCategory === category
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Album Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredAlbums.map((album) => {
              const Icon = album.icon
              return (
                <button
                  key={album.id}
                  onClick={() => setSelectedAlbum(album)}
                  className="group text-left overflow-hidden transition-colors hover:bg-white dark:hover:bg-gray-800/50"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={album.cover}
                      alt={`${album.title} - Synergy Multimedia Academy Kampala student work and campus gallery`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3">
                      <span className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        {album.id === 'videos' ? <Film size={12} /> : <Image size={12} />}
                        {album.items.length} {album.id === 'videos' ? 'videos' : 'photos'}
                      </span>
                    </div>
                  </div>
                  <div className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={18} className="text-primary-500 dark:text-primary-400" />
                      <h3 className="font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {album.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                      {album.description}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
          </div>
        ) : (
          /* Album Detail View */
          <div>
            <button
              onClick={() => setSelectedAlbum(null)}
              className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium mb-6 transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Albums
            </button>

            <div className="mb-8">
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">{selectedAlbum.title}</h2>
              <p className="text-gray-500 dark:text-gray-400 mt-2">{selectedAlbum.description}</p>
            </div>

            {selectedAlbum.id === 'videos' ? (
              /* Video Grid */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {selectedAlbum.items.map((item, index) => (
                  <div key={index} className="overflow-hidden">
                    {item.youtubeId ? (
                      <iframe
                        className="w-full aspect-video bg-black"
                        src={`https://www.youtube.com/embed/${item.youtubeId}`}
                        title={item.caption}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        className="w-full aspect-video bg-black"
                        controls
                        preload="metadata"
                      >
                        <source src={item.src} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    )}
                    <div className="p-4">
                      <div className="flex items-center gap-2">
                        <Play size={16} className="text-primary-500 dark:text-primary-400" />
                        <p className="font-medium text-gray-800 dark:text-gray-200">{item.caption}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : selectedAlbum.id === 'ai-generated' ? (
              /* AI Prompt & Result Grid */
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {selectedAlbum.items.map((item, index) => (
                  <div key={index} className="overflow-hidden border-b border-gray-200 dark:border-gray-800 pb-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Prompt Side */}
                      <div className="flex flex-col justify-center bg-gray-50 dark:bg-gray-800/50 p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Wand2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                          <span className="text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wider">Student Prompt</span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 text-sm italic leading-relaxed mb-4">
                          "{item.prompt}"
                        </p>
                        <div className="mt-auto">
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">
                            <Sparkles className="w-3.5 h-3.5 text-primary-500" />
                            {item.tool}
                          </span>
                        </div>
                      </div>
                      
                      {/* Result Side */}
                      <div className="relative group">
                        <div className="absolute -top-2 -right-2 z-10 bg-green-500 text-white px-3 py-1 text-xs font-bold">
                          ✓ Result
                        </div>
                        <button
                          onClick={() => setLightboxIndex(index)}
                          className="relative aspect-square w-full overflow-hidden"
                        >
                          <img
                            src={item.src}
                            alt={item.caption}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                        </button>
                        <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300 text-center">{item.caption}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Photo Grid */
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {selectedAlbum.items.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => setLightboxIndex(index)}
                    className="group relative aspect-square overflow-hidden"
                  >
                    <img
                      src={item.src}
                      alt={item.caption}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && currentItem && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          {/* Close */}
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 text-white/70 hover:text-white z-10 p-2"
          >
            <X size={28} />
          </button>

          {/* Counter */}
          <div className="absolute top-5 left-5 text-white/70 text-sm font-medium">
            {lightboxIndex + 1} / {selectedAlbum.items.length}
          </div>

          {/* Previous */}
          <button
            onClick={() => setLightboxIndex((prev) => (prev - 1 + selectedAlbum.items.length) % selectedAlbum.items.length)}
            className="absolute left-2 sm:left-6 text-white/60 hover:text-white z-10 p-2"
          >
            <ChevronLeft size={36} />
          </button>

          {/* Image */}
          <div className="max-w-5xl max-h-[85vh] mx-4">
            {currentItem.type === 'video' ? (
              <video className="max-h-[80vh] mx-auto rounded-lg" controls autoPlay>
                <source src={currentItem.src} type="video/mp4" />
              </video>
            ) : (
              <img
                src={currentItem.src}
                alt={`${currentItem.caption} - SMA student portfolio work Kampala`}
                className="max-h-[80vh] max-w-full mx-auto rounded-lg object-contain"
                loading="lazy"
                decoding="async"
              />
            )}
            <p className="text-white/80 text-center mt-4 text-sm font-medium">
              {currentItem.caption}
            </p>
          </div>

          {/* Next */}
          <button
            onClick={() => setLightboxIndex((prev) => (prev + 1) % selectedAlbum.items.length)}
            className="absolute right-2 sm:right-6 text-white/60 hover:text-white z-10 p-2"
          >
            <ChevronRight size={36} />
          </button>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-gray-950 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Synergy Concepts Hub. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}
